/**
 * Demo sandbox chip and persona switcher (docs/features/demo-sandbox/plan.md §4.3, §4.4).
 * Header chip "Sandbox · N days left"; its menu shows when the sandbox is deleted and lets the
 * prospect view the app as each seeded persona, or go back to their own account.
 * Renders nothing outside a sandbox tenant.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FlaskConical, UserRound, ArrowLeftRight, LogIn, CalendarPlus } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { daysUntil, getActivePersona } from '../../utils/sandbox';
import { backToOwner, switchToPersona } from '../../services/demo/personaSwitch';
import SandboxService from '../../services/demo/sandbox.service';

const SALES_EMAIL = 'sales@pelorahealth.com';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const SandboxChip: React.FC = () => {
  const { user, refreshPermissions } = useAuth();
  const sandbox = user?.sandbox;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const active = getActivePersona();

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!sandbox) return null;
  const trialDays = user?.subscription?.trialDaysLeft;
  const daysLeft = typeof trialDays === 'number' ? trialDays : daysUntil(sandbox.deleteAt);
  const personas = sandbox.personas.filter((p) => p.key !== active?.key);

  const choose = async (key: string) => {
    setBusy(key);
    setError(null);
    try {
      await switchToPersona(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch persona');
      setBusy(null);
    }
  };

  const extend = async () => {
    setBusy('extend');
    setError(null);
    try {
      await SandboxService.extend();
      await refreshPermissions(); // new delete date, trial days and canExtend
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not extend the sandbox');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="sandbox-chip"
      >
        <FlaskConical size={14} aria-hidden="true" />
        Sandbox{daysLeft !== null ? ` · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : ''}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-lg z-50">
          <p className="text-gray-600">
            Sample data only. This sandbox is deleted on <span className="font-medium text-gray-900">{formatDate(sandbox.deleteAt)}</span>.
          </p>
          {sandbox.seed.status !== 'ready' && <p className="mt-2 text-gray-500">Sample agency is still being set up…</p>}
          {sandbox.canExtend && !active && (
            <button type="button" role="menuitem" disabled={busy !== null} onClick={extend}
              className="mt-3 flex w-full items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-gray-900 hover:bg-gray-50 disabled:opacity-60">
              <CalendarPlus size={14} aria-hidden="true" /> {busy === 'extend' ? 'Extending…' : 'Extend 7 days (once)'}
            </button>
          )}
          {active && (
            <button type="button" role="menuitem" onClick={() => backToOwner()}
              className="mt-3 flex w-full items-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-white hover:bg-slate-700">
              <LogIn size={14} aria-hidden="true" /> Back to my account
            </button>
          )}
          {personas.length > 0 && (
            <>
              <p className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">View as</p>
              <ul className="space-y-1">
                {personas.map((p) => (
                  <li key={p.key}>
                    <button type="button" role="menuitem" disabled={busy !== null} onClick={() => choose(p.key)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-100 disabled:opacity-60">
                      {p.portal ? <UserRound size={14} aria-hidden="true" /> : <ArrowLeftRight size={14} aria-hidden="true" />}
                      <span className="flex-1">
                        <span className="block text-gray-900">{p.name || p.key}</span>
                        <span className="block text-xs text-gray-500">{p.portal ? `Family portal · ${p.title || 'Guardian'}` : p.title || p.roles.join(', ')}</span>
                      </span>
                      {busy === p.key && <span className="text-xs text-gray-500">Switching…</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {error && <p role="alert" className="mt-2 text-red-600">{error}</p>}
          <a href={`mailto:${SALES_EMAIL}?subject=Pelora%20demo`} className="mt-3 block text-blue-600 hover:text-blue-700">Talk to sales</a>
        </div>
      )}
    </div>
  );
};

export default SandboxChip;
